import React, { useState, useEffect, useRef } from 'react';
import {
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  Brain,
  Eye,
  ShieldAlert,
  Activity,
  ChevronDown,
  ChevronUp,
  Bot,
  User,
  Sparkles,
  RotateCcw,
  Check,
  X
} from 'lucide-react';
import Page from './Page';
import { utils } from './utils/index';

const API_BASE_URL = utils.config.REACT_APP_API_HOST || 'http://localhost:4000';

/**
 * Collapsible accordion for step-by-step thinking process.
 * 
 * @param {Object} params - The options object.
 * @param {Array<Object>} params.steps - Collection of execution step logs.
 * @param {boolean} params.isRunning - Indicates if the agent process is still active.
 * @returns {JSX.Element|null} Rendered collapsible list.
 */
function ThinkingProcess({ steps, isRunning }) {
  const [isExpanded, setIsExpanded] = useState(isRunning);

  useEffect(() => {
    if (isRunning) {
      setIsExpanded(true);
    }
  }, [isRunning]);

  /**
   * Resolves icon based on step action type.
   * 
   * @param {Object} params - Options object.
   * @param {string} params.type - Action type.
   * @returns {JSX.Element} Visual icon component.
   */
  const getStepIcon = ({ type }) => {
    switch (type) {
      case 'thought':
        return <Brain className="w-3.5 h-3.5 text-amber-500" />;
      case 'action':
        return <Eye className="w-3.5 h-3.5 text-indigo-500" />;
      case 'warning':
        return <ShieldAlert className="w-3.5 h-3.5 text-rose-500" />;
      case 'success':
        return <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />;
      case 'error':
        return <AlertCircle className="w-3.5 h-3.5 text-rose-600" />;
      default:
        return <Activity className="w-3.5 h-3.5 text-slate-400" />;
    }
  };

  if (!steps || steps.length === 0) return null;

  return (
    <div className="mb-3 rounded-xl border border-slate-200/80 bg-slate-50/80 overflow-hidden text-xs">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-3 py-2 flex items-center justify-between text-slate-600 font-medium hover:bg-slate-100/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isRunning ? (
            <Loader2 className="w-3.5 h-3.5 text-indigo-600 animate-spin" />
          ) : (
            <Brain className="w-3.5 h-3.5 text-slate-500" />
          )}
          <span>{isRunning ? 'הסוכן מעבד את הנתונים...' : `תהליך חשיבה (${steps.length} צעדים)`}</span>
        </div>
        {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {isExpanded && (
        <div className="p-3 border-t border-slate-200/60 space-y-2 bg-white/50 max-h-56 overflow-y-auto">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-start gap-2 text-slate-700">
              <div className="mt-0.5 flex-shrink-0">{getStepIcon({ type: step.type })}</div>
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-[11px] text-slate-500 block">{step.title}</span>
                <p className="text-[11px] leading-relaxed break-words">{step.text}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Main General Conversational AI Agent Chat Interface Component using custom fetch SSE streaming.
 * 
 * @returns {JSX.Element} Rendered AgentDashboard chat element.
 */
export default function AgentDashboard() {
  const [inputTask, setInputTask] = useState('');
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('idle'); // 'idle' | 'running' | 'requires_approval' | 'error'
  const [conversationId, setConversationId] = useState(null);
  const [pendingApprovalData, setPendingApprovalData] = useState(null);

  const textareaRef = useRef(null);
  const chatEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (messages.length > 0) {
      requestAnimationFrame(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.max(textareaRef.current.scrollHeight, 80)}px`;
    }
  }, [inputTask]);

  // Clean up active fetch stream controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Retrieves authorization token safely from LocalStorage.
   * 
   * @returns {string|null} Token string or null if missing.
   * @throws {Error} Throws error if localStorage read fails.
   */
  const getAuthToken = () => {
    try {
      const token = localStorage.getItem('token');
      console.debug('[Auth] Reading authentication token from local storage', { tokenExists: Boolean(token) });
      return token;
    } catch (err) {
      console.error('[Auth Error] Failed reading token from local storage:', err.message);
      throw new Error('Authentication storage access failure');
    }
  };

  /**
   * Cleans input text string.
   * 
   * @param {Object} params - Options.
   * @param {string} params.text - Input text.
   * @returns {string} Sanitized string.
   */
  const sanitizeText = ({ text }) => {
    if (typeof text !== 'string') return '';
    return text.trim();
  };

  /**
   * Connects to SSE stream using fetch API to allow standard Authorization Bearer header.
   * 
   * @param {Object} params - Parameter object.
   * @param {string} params.activeId - Session ID.
   * @param {string} params.promptText - Prompt text message.
   * @returns {Promise<void>}
   * @throws {Error} Throws error if stream connection fails.
   */
  const startSseConnection = async ({ activeId, promptText }) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const token = getAuthToken();
    const url = `${API_BASE_URL}/api/chat/stream?conversationId=${encodeURIComponent(activeId)}&message=${encodeURIComponent(promptText)}`;
    
    const headers = {
      'Accept': 'text/event-stream'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.info('[SSE Stream] Initiating fetch-based SSE stream connection', { activeId });

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP stream request failed with status code ${response.status}`);
      }

      if (!response.body) {
        throw new Error('Readable stream body is not available');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const block of lines) {
          if (!block.trim()) continue;

          let eventName = 'message';
          let eventData = '';

          const blockLines = block.split('\n');
          for (const line of blockLines) {
            if (line.startsWith('event:')) {
              eventName = line.replace('event:', '').trim();
            } else if (line.startsWith('data:')) {
              eventData = line.replace('data:', '').trim();
            }
          }

          if (!eventData) continue;

          // Dispatch event handlers according to event types
          if (eventName === 'step') {
            const stepData = JSON.parse(eventData);
            setMessages((prev) => {
              const next = [...prev];
              const lastIdx = next.length - 1;
              if (lastIdx >= 0 && next[lastIdx].role === 'assistant') {
                next[lastIdx] = {
                  ...next[lastIdx],
                  steps: [...(next[lastIdx].steps || []), stepData]
                };
              }
              return next;
            });
          } else if (eventName === 'requires_approval') {
            const eventPayload = JSON.parse(eventData);
            setStatus('requires_approval');
            setPendingApprovalData(eventPayload.pendingAction);

            setMessages((prev) => {
              const next = [...prev];
              const lastIdx = next.length - 1;
              if (lastIdx >= 0 && next[lastIdx].role === 'assistant') {
                next[lastIdx] = {
                  ...next[lastIdx],
                  isExecuting: false,
                  text: '🛡️ נדרש אישור לביצוע פעולה רגישה...'
                };
              }
              return next;
            });
            return;
          } else if (eventName === 'completed') {
            const eventPayload = JSON.parse(eventData);
            setStatus('idle');

            setMessages((prev) => {
              const next = [...prev];
              const lastIdx = next.length - 1;
              if (lastIdx >= 0 && next[lastIdx].role === 'assistant') {
                next[lastIdx] = {
                  ...next[lastIdx],
                  isExecuting: false,
                  text: eventPayload.response
                };
              }
              return next;
            });
            return;
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        console.info('[SSE Stream] Stream reader aborted manually');
        return;
      }

      console.error('[SSE Stream Error] Stream processing failure:', err.message);
      setStatus('error');

      setMessages((prev) => {
        const next = [...prev];
        const lastIdx = next.length - 1;
        if (lastIdx >= 0 && next[lastIdx].role === 'assistant') {
          next[lastIdx] = {
            ...next[lastIdx],
            isExecuting: false,
            text: 'ארעה שגיאה בתקשורת מול השרת בזמן הזרמת הנתונים.'
          };
        }
        return next;
      });
    }
  };

  /**
   * Dispatches new prompt and initializes stream.
   * 
   * @param {Object} params - Parameter container.
   * @param {React.FormEvent} [params.event] - Form submit event.
   * @returns {void}
   */
  const handleStart = ({ event } = {}) => {
    if (event) event.preventDefault();
    const cleanTask = sanitizeText({ text: inputTask });
    if (!cleanTask || status === 'running') return;

    const activeConversationId = conversationId || `chat_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    if (!conversationId) {
      setConversationId(activeConversationId);
    }

    const userMessage = { id: Date.now().toString(), role: 'user', text: cleanTask };
    const assistantMessage = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      text: '',
      steps: [],
      isExecuting: true
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInputTask('');
    setStatus('running');

    startSseConnection({ activeId: activeConversationId, promptText: cleanTask });
  };

  /**
   * Responds to Human-in-the-Loop approval requests using Bearer token authorization header.
   * 
   * @param {Object} params - Parameters object.
   * @param {boolean} params.approved - User response boolean.
   * @returns {Promise<void>}
   * @throws {Error} Throws network or HTTP execution errors.
   */
  const handleHitlResponse = async ({ approved }) => {
    if (!conversationId) return;

    setStatus('running');
    setPendingApprovalData(null);

    try {
      const token = getAuthToken();
      const headers = { 'Content-Type': 'application/json' };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.info('[HITL] Submitting HITL decision payload', { conversationId, approved });

      const response = await fetch(`${API_BASE_URL}/api/hitl/respond`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ conversationId, approved })
      });

      if (!response.ok) {
        throw new Error(`Server responded with status code ${response.status}`);
      }

      const resData = await response.json();
      if (!resData.success) throw new Error(resData.error || 'Failed to submit authorization decision.');

      startSseConnection({ activeId: conversationId, promptText: approved ? 'הפעולה אושרה לבצע.' : 'הפעולה נדחתה.' });

    } catch (error) {
      console.error('[HITL Error] Failed handling HITL response dispatch:', error.message);
      setStatus('error');
    }
  };

  /**
   * Resets entire conversation state.
   * 
   * @returns {void}
   */
  const handleClearChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setMessages([]);
    setStatus('idle');
    setConversationId(null);
    setPendingApprovalData(null);
    setInputTask('');
  };

  /**
   * Key handling for textarea submit.
   * 
   * @param {Object} params - Parameter object.
   * @param {React.KeyboardEvent} params.event - Keyboard event.
   * @returns {void}
   */
  const handleKeyDown = ({ event }) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleStart({ event });
    }
  };

  return (
    <Page title="סוכן AI">
      <div className="w-full max-w-4xl mx-auto flex flex-col space-y-4" dir="rtl">
        
        {/* Header */}
        <div className="flex items-center justify-between px-2 py-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" />
            <h2 className="text-base font-bold text-slate-800">סוכן ה-AI האישי שלך (SSE Enabled)</h2>
          </div>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={handleClearChat}
              className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors bg-white border border-slate-200/80 px-2.5 py-1.5 rounded-lg shadow-2xs"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>שיחה חדשה</span>
            </button>
          )}
        </div>

        {/* Chat Window */}
        <div className="w-full bg-slate-50/50 border border-slate-200/80 rounded-2xl p-4 sm:p-6 min-h-[350px] max-h-[550px] overflow-y-auto space-y-4 shadow-2xs">
          {messages.length === 0 ? (
            <div className="py-12 flex flex-col items-center justify-center text-center space-y-3 text-slate-400">
              <div className="w-14 h-14 rounded-2xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 mb-1 shadow-2xs">
                <Bot className="w-7 h-7" />
              </div>
              <h3 className="text-base font-bold text-slate-800">שלום! במה אוכל לעזור לך היום?</h3>
              <p className="text-xs max-w-md text-slate-500 leading-relaxed">
                רשום לי כל שאלה, והשלבים יופיעו בזמן אמת בשידור חי.
              </p>
            </div>
          ) : (
            messages.map((msg) => {
              const isUser = msg.role === 'user';
              return (
                <div
                  key={msg.id}
                  className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  <div
                    className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                      isUser ? 'bg-indigo-600 text-white shadow-2xs' : 'bg-slate-800 text-white shadow-2xs'
                    }`}
                  >
                    {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>

                  <div
                    className={`max-w-[85%] sm:max-w-[75%] rounded-2xl p-4 text-sm leading-relaxed ${
                      isUser
                        ? 'bg-indigo-600 text-white rounded-tr-none'
                        : 'bg-white border border-slate-200/80 text-slate-800 rounded-tl-none shadow-2xs'
                    }`}
                  >
                    {!isUser && msg.steps && (
                      <ThinkingProcess steps={msg.steps} isRunning={msg.isExecuting} />
                    )}

                    <div className="whitespace-pre-wrap break-words">
                      {msg.text || (msg.isExecuting && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={chatEndRef} />
        </div>

        {/* HITL Card */}
        {status === 'requires_approval' && pendingApprovalData && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl shadow-sm flex flex-col gap-3">
            <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              <span>נדרש אישור לביצוע פעולה רגישה</span>
            </div>
            <div className="text-xs text-amber-900 space-y-1 bg-white/70 p-3 rounded-xl border border-amber-100 font-mono dir-ltr text-left overflow-x-auto">
              <div><strong>Agent:</strong> {pendingApprovalData.agentName}</div>
              <div><strong>Operation:</strong> {pendingApprovalData.opName}</div>
              <div className="whitespace-pre-wrap break-all">
                <strong>Argument:</strong>{' '}
                {typeof pendingApprovalData.argument === 'object'
                  ? JSON.stringify(pendingApprovalData.argument, null, 2)
                  : String(pendingApprovalData.argument)}
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => handleHitlResponse({ approved: false })}
                className="px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 rounded-xl text-xs font-semibold flex items-center gap-1 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                <span>דחה פעולה</span>
              </button>
              <button
                type="button"
                onClick={() => handleHitlResponse({ approved: true })}
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-semibold flex items-center gap-1 shadow-2xs transition-colors"
              >
                <Check className="w-3.5 h-3.5" />
                <span>אשר ובצע</span>
              </button>
            </div>
          </div>
        )}

        {/* Input */}
        <div className="bg-white border border-slate-200/90 rounded-2xl p-3 shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/10 transition-all">
          <form onSubmit={(e) => handleStart({ event: e })} className="flex flex-col gap-2">
            <textarea
              ref={textareaRef}
              value={inputTask}
              onChange={(e) => setInputTask(e.target.value)}
              onKeyDown={(e) => handleKeyDown({ event: e })}
              disabled={status === 'running' || status === 'requires_approval'}
              placeholder="רשום הודעה לסוכן... (Enter לשליחה)"
              className="w-full bg-transparent px-2 pt-1 text-sm text-slate-800 placeholder-slate-400 focus:outline-none resize-none min-h-[80px] max-h-40 leading-relaxed border-none ring-0"
            />

            <div className="flex items-center justify-between pt-2 border-t border-slate-100 px-1">
              <span className="text-[11px] text-slate-400">
                AI יכול לטעות יש לבדוק את התשובות
              </span>

              <button
                type="submit"
                disabled={!inputTask.trim() || status === 'running' || status === 'requires_approval'}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-100 disabled:text-slate-300 text-white rounded-xl text-xs font-medium shadow-2xs transition-all active:scale-95 flex items-center gap-1.5"
              >
                <span>שליחה</span>
                {status === 'running' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : ( 
                  <Send className="w-3.5 h-3.5" />
                )}
              </button> 
            </div>
          </form>   
        </div>

      </div>
    </Page>
  );
}